/**
 * Claude API service - calls serverless function to keep API key secure
 */

const API_ENDPOINT = '/api/claude';

/**
 * Generate itinerary using Claude API via serverless function
 */
export const generateItinerary = async (tripData) => {
  console.log('=== Generating Itinerary ===');

  const systemPrompt = `You are a travel planning assistant that creates realistic, well-reasoned day itineraries.

Your job is to take a list of places a user wants to visit and create two plans:
1. Plan A (Realistic): A comfortable itinerary that the user can actually achieve
2. Plan B (Ambitious): Everything they asked for, with clear warnings about tradeoffs

Key principles:
- Be realistic about visit durations. A major museum needs 2+ hours. A quick photo spot needs 10 minutes. A sit-down meal needs 60-90 minutes.
- Respect time-locked items as fixed anchors. Build the route around them.
- When optimization mode is "optimize_route": Order stops to minimize total travel time between destinations. Find the most time-efficient route that visits all places.
- When optimization mode is "respect_priorities": Prioritize "Must See" items over "Want to See" over "If Time Allows"
- Default to walking and public transit. Suggest walking for distances under 15-20 minutes; transit for longer.
- Always explain your reasoning for the order and any cuts.
- Factor in buffer time between stops (5-10 min) for transitions.
- If you don't have specific opening hours, make reasonable assumptions and note them.

When something won't fit, be direct about it and explain why.

Return your response as valid JSON matching this structure:
{
  "plan_a": {
    "title": "Realistic Plan",
    "stops": [
      {
        "order": 1,
        "name": "Place Name",
        "arrival_time": "10:15",
        "departure_time": "11:30",
        "duration_minutes": 75,
        "duration_reasoning": "Explanation",
        "transport_to_next": {
          "mode": "walk",
          "duration_minutes": 12,
          "notes": "Description"
        }
      }
    ],
    "summary": "Summary text",
    "cuts": [
      {
        "name": "Place Name",
        "reason": "Reason why it was cut"
      }
    ]
  },
  "plan_b": {
    "title": "Ambitious Plan",
    "stops": [...],
    "warnings": ["Warning 1", "Warning 2"]
  },
  "reasoning": "Overall explanation of the itinerary order and decisions"
}`;

  const userPrompt = `Create a day itinerary for ${tripData.city.name} on ${tripData.date}.

**Time window:** ${tripData.startTime} to ${tripData.endTime}
**Starting point:** ${tripData.startLocation.name}
**Ending point:** ${tripData.endLocation?.name || tripData.startLocation.name}

**Places to visit:**
${tripData.places.map(place => {
  let line = `- ${place.name} (${place.priority})`;
  if (place.lockedTime) {
    line += ` - LOCKED at ${place.lockedTime}`;
  }
  return line;
}).join('\n')}

**Mode:** ${tripData.optimizationMode}

Please generate Plan A (realistic) and Plan B (ambitious) as JSON.`;

  // Models to try in order of preference
  const modelsToTry = [
    'claude-sonnet-4-20250514',
    'claude-3-5-sonnet-20241022',
    'claude-3-5-sonnet-20240620',
    'claude-3-sonnet-20240229',
    'claude-3-haiku-20240307'
  ];

  let lastError;

  for (const modelName of modelsToTry) {
    try {
      console.log(`Trying model: ${modelName}`);

      const response = await fetch(API_ENDPOINT, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          model: modelName,
          max_tokens: 4096,
          system: systemPrompt,
          messages: [
            {
              role: 'user',
              content: userPrompt,
            },
          ],
        }),
      });

      const data = await response.json();

      if (!response.ok) {
        console.warn(`Model ${modelName} failed:`, data.error?.message || data.error);
        lastError = new Error(data.error?.message || 'API request failed');
        continue;
      }

      console.log(`Successfully used model: ${modelName}`);

      const content = data.content[0];
      if (content.type === 'text') {
        // Extract JSON from the response
        const jsonMatch = content.text.match(/\{[\s\S]*\}/);
        if (jsonMatch) {
          try {
            // Clean the JSON string to remove control characters
            let jsonString = jsonMatch[0];
            // Remove control characters except newlines and tabs
            // eslint-disable-next-line no-control-regex
            jsonString = jsonString.replace(/[\x00-\x08\x0B-\x0C\x0E-\x1F\x7F]/g, '');
            return JSON.parse(jsonString);
          } catch (parseError) {
            console.error('JSON parse error:', parseError);
            // Try to fix common JSON issues
            let jsonString = jsonMatch[0];
            // Remove control characters
            // eslint-disable-next-line no-control-regex
            jsonString = jsonString.replace(/[\x00-\x1F\x7F]/g, '');
            try {
              return JSON.parse(jsonString);
            } catch (secondError) {
              throw new Error(`Failed to parse Claude response as JSON: ${parseError.message}`);
            }
          }
        }
        throw new Error('No JSON found in Claude response');
      }
      throw new Error('Unexpected response format from Claude');
    } catch (error) {
      console.warn(`Model ${modelName} failed:`, error.message);
      lastError = error;
    }
  }

  throw lastError || new Error('All model attempts failed');
};
